"use client"

/**
 * Unified tablet + desktop listing-detail info column.
 *
 * Path B step 1 of the responsive-triplet fix (see
 * `.tondocs/infra/HANDOFF_LISTING_DETAIL_TRIPLET_2026_07_23.md` and
 * `.tondocs/feed/OVERLAY_FSM_SPEC.md` §E Phase 2). Previously the tablet
 * (`product-details-tablet.tsx`) and desktop (`product-details.tsx`) columns
 * were two sibling components, each rendered in its own `md:block lg:hidden` /
 * `hidden lg:block` grid inside `product-detail-view.tsx`. `display:none` does
 * not stop React from mounting, so `OrderPartyCard` (+ its data-fetching
 * hooks) mounted once per variant. This single component renders once for
 * md+lg, cutting that pair of mounts to one.
 *
 * Layout parity: base (unprefixed) classes reproduce the OLD tablet at md;
 * `lg:` overrides reproduce the OLD desktop at lg. The one non-CSS asymmetry —
 * tablet placed the price/CTA block near the top, desktop placed it below the
 * description — is handled with flex `order`: DOM order = tablet order, and at
 * lg the price block (`lg:order-1`) and footer (`lg:order-2`) reorder to the
 * desktop sequence. The `chatLayout` asymmetry (tablet sheet vs desktop
 * dialog) is preserved via `useMediaQuery` since a single OrderPartyCard
 * instance can only receive one value.
 */

import { useTranslations } from "next-intl";

import { MapPin, Clock, AlertTriangle } from "lucide-react"
import { UnifiedAddToCartButton } from "@/components/UnifiedAddToCartButton"
import { formatPrice } from "@/lib/utils"
import { ReportMenu } from "@/components/ui/report-menu"
import { OrderPartyCard } from "@/components/orders/order-party-card"
import { SellerCheckoutNoteBanner } from "@/components/cart/seller-checkout-note-banner"
import { ListingConditionBadges } from "@/components/detailed-listing-view/listing-condition-badges"
import { AuctionPanel, type AuctionData } from "@/components/detailed-listing-view/auction-panel"
import { useAuction } from "@/hooks/use-auction"
import { TcgCardDetails, type TcgCardDetailsData } from "@/components/detailed-listing-view/tcg-card-details"
import { PreorderTimelineSection } from "@/components/listings/preorder-timeline-section"
import { MakeOfferButton } from "@/components/offers/make-offer-button"
import { ListingSplitCommerceCta } from "@/components/offers/listing-split-commerce-cta"
import { ListingOffersPanel } from "@/components/offers/listing-offers-panel"
import { ListingOwnerEditButton } from "@/components/detailed-listing-view/listing-owner-edit-button"
import { useMediaQuery } from "@/hooks/use-media-query"



interface ProductDetailsProps {
  title: string
  price: string
  currency?: string | null
  condition: string
  grading: string
  description: string
  location: string
  createdAt: string
  images?: string[]
  shippingMethods?: any[]
  seller: {
    id: string
    username: string
    avatar_url?: string
    followers_count: number
    is_following: boolean
    sales_count?: number
    checkout_note?: string | null
  }
  currentUserId?: string
  itemId?: string
  stock?: number
  onFollowChangeAction: (userId: string, next: boolean) => void
  scheduledPublishAt?: string | null
  sellerBanned?: boolean
  isUnlisted?: boolean
  auction?: AuctionData | null
  onPayAsWinner?: () => void
  auctionCountryBanWarning?: string | null
  cardDetails?: TcgCardDetailsData | null
  isPreorder?: boolean
  listingFormat?: string | null
  // Phase 3 (2026-06-29): `acceptsOffers` prop dropped — derive from
  // `listingFormat` via `isOfferEnabledFormat()` (the column collapsed).
  autoDeclineBelow?: number | null
}

export default function ProductDetails({
  title,
  price,
  currency,
  condition,
  grading,
  description,
  location,
  createdAt,
  images = [],
  shippingMethods = [],
  seller,
  currentUserId,
  itemId,
  stock,
  onFollowChangeAction,
  scheduledPublishAt = null,
  sellerBanned = false,
  isUnlisted = false,
  auction = null,
  onPayAsWinner,
  auctionCountryBanWarning = null,
  cardDetails = null,
  isPreorder = false,
  listingFormat = null,
  autoDeclineBelow = null,
}: ProductDetailsProps) {

  const tListing = useTranslations("listing");
  const isOwner = Boolean(currentUserId && currentUserId === seller.id)
  // Phase 3 (2026-06-29): "offers enabled" derives purely from listing_format
  // (the legacy column collapsed). `offersAllowed` is the merged predicate.
  const isOffersOnly = listingFormat === "offers_only"
  const isFixedWithOffers = listingFormat === "fixed_price_with_offers"
  const offersAllowed = isOffersOnly || isFixedWithOffers
  const canSplit = isFixedWithOffers && !isOwner
  void autoDeclineBelow

  // Preserve the tablet-vs-desktop chat surface asymmetry from a single mount:
  // desktop (lg+) opened chat as a centered dialog, tablet (md) as a slide-in
  // sheet. `chatLayout` is read at render but only matters once chat opens
  // (post-mount, after the client matchMedia settles), so an SSR-safe
  // `useMediaQuery` is exact here without hydration flicker.
  const isLgUp = useMediaQuery("(min-width: 1024px)")

  const formatRelativeTime = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (diff < 60) return tListing("relativeTime.justNow");
    if (diff < 3600) return tListing("relativeTime.minutesAgo", { count: Math.floor(diff / 60) });
    if (diff < 86400) return tListing("relativeTime.hoursAgo", { count: Math.floor(diff / 3600) });
    if (diff < 2592000) return tListing("relativeTime.daysAgo", { count: Math.floor(diff / 86400) });
    if (diff < 31536000) return tListing("relativeTime.monthsAgo", { count: Math.floor(diff / 2592000) });
    return tListing("relativeTime.yearsAgo", { count: Math.floor(diff / 31536000) });
  };

  const formattedDate = formatRelativeTime(createdAt)
  const auctionSlot = useAuction(auction && itemId ? itemId : null)
  const effectiveAuctionStatus = auctionSlot?.auctionStatus ?? auction?.status
  const isAuctionEnded = effectiveAuctionStatus === "ended" || effectiveAuctionStatus === "settled"

  return (
    <div className="flex flex-col gap-6 lg:gap-4 w-full text-foreground px-4 lg:pl-0 lg:pr-6">
      {/* Title row — always visible at md+ (tablet showed it; desktop showed it via lg:flex) */}
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-bold wrap-break-word text-foreground">{title}</h1>
        {itemId ? (
          isOwner ? (
            <ListingOwnerEditButton
              variant="header-icon"
              listingId={itemId}
              title={title}
              price={price}
              currency={currency}
              images={images}
              description={description}
              condition={condition}
              listingFormat={listingFormat}
            />
          ) : (
            <ReportMenu
              itemType="listing"
              itemId={itemId}
              itemTitle={title}
              currentUserId={currentUserId}
              reportedUserId={seller.id}
              reportedUsername={seller.username}
              reportedAvatarUrl={seller.avatar_url ?? null}
              triggerClassName="rounded-full h-10 w-10 inline-flex items-center justify-center hover:bg-muted"
              iconClassName="h-5 w-5"
            />
          )
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {cardDetails ? (
          <TcgCardDetails card={cardDetails} />
        ) : (
          <ListingConditionBadges condition={condition} grade={grading} />
        )}
        {auction ? (
          isAuctionEnded ? (
            <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
              {tListing("ended")}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium border border-green-500/30">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              {tListing("live")}
            </span>
          )
        ) : null}
      </div>

      {/* Price / CTA block. Tablet: near top (border-b below it). Desktop:
          below the description (`lg:order-1`, border-t above it). */}
      {!auction ? (
        <div className="flex flex-col gap-2 pb-2 border-b border-border lg:order-1 lg:border-b-0 lg:border-t lg:pt-4">
          <div
            className={`flex items-center gap-4 ${!isOwner && !isOffersOnly ? "justify-between" : ""}`}
          >
            {!isOffersOnly && (
              <div className="text-2xl font-bold shrink-0 text-foreground">
                {formatPrice(price, currency || "USD")}
              </div>
            )}
            {!isOffersOnly && !isOwner ? (
              <div className="min-w-0 flex-1 -mb-4">
                {canSplit && itemId ? (
                  <ListingSplitCommerceCta
                    unified={{
                      price,
                      currency,
                      listingId: itemId,
                      stock: stock,
                      currentUserId,
                      name: title,
                      images,
                      sellerId: seller.id,
                      sellerName: seller.username,
                      condition,
                      description,
                      shippingMethods,
                      context: "product-page",
                      lazy: false,
                      scheduledPublishAt,
                      sellerBanned,
                      isUnlisted,
                      listingType: isPreorder ? "preorder" : null,
                    }}
                    offer={{
                      listingId: itemId,
                      currentUserId: currentUserId ?? null,
                      listingPrice: Number(price),
                      currency: currency ?? null,
                      listingTitle: title,
                      listingImageUrl: images[0] ?? null,
                    }}
                  />
                ) : itemId ? (
                  <UnifiedAddToCartButton
                    price={price}
                    currency={currency}
                    listingId={itemId}
                    stock={stock}
                    currentUserId={currentUserId}
                    name={title}
                    images={images}
                    sellerId={seller.id}
                    sellerName={seller.username}
                    condition={condition}
                    description={description}
                    shippingMethods={shippingMethods}
                    context="product-page"
                    lazy={false}
                    scheduledPublishAt={scheduledPublishAt}
                    sellerBanned={sellerBanned}
                    isUnlisted={isUnlisted}
                    listingType={isPreorder ? "preorder" : null}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {offersAllowed && !isOwner && itemId && !canSplit ? (
            <MakeOfferButton
              listingId={itemId}
              currentUserId={currentUserId ?? null}
              sellerId={seller.id}
              listingFormat={listingFormat ?? "fixed_price"}
              listingPrice={isOffersOnly ? null : Number(price)}
              currency={currency ?? null}
              listingTitle={title}
              listingImageUrl={images[0] ?? null}
              listingCondition={condition}
              listingGrade={grading}
              sellerUsername={seller.username}
              fullWidth
            />
          ) : null}
          {isOwner && offersAllowed && itemId && currentUserId ? (
            <ListingOffersPanel
              listingId={itemId}
              currentUserId={currentUserId}
              currency={currency ?? null}
              className="mt-3"
            />
          ) : null}
        </div>
      ) : null}

      <div>
        <OrderPartyCard
          role="seller"
          name={seller.username}
          username={seller.username}
          avatarUrl={seller.avatar_url ?? null}
          userId={seller.id}
          currentUserId={currentUserId}
          initialFollowing={Boolean(seller.is_following)}
          onFollowChange={(next) => onFollowChangeAction(seller.id, next)}
          salesCount={seller.sales_count}
          followersCount={seller.followers_count}
          surface="plain"
          messageVariant="secondary"
          chatLayout={isLgUp ? "dialog" : "sheet"}
          currentListing={
            itemId
              ? {
                  id: itemId,
                  title,
                  price,
                  image_urls: images,
                  condition,
                  grade: grading,
                }
              : null
          }
        />
      </div>

      <SellerCheckoutNoteBanner note={seller.checkout_note ?? ""} className="lg:mt-1" />

      {auction && auctionCountryBanWarning ? (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/20 border border-amber-800/30">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-200">{auctionCountryBanWarning}</p>
        </div>
      ) : null}

      {auction ? (
        <div>
          <AuctionPanel
            listingId={itemId ?? ""}
            auction={auction}
            currency={currency}
            currentUserId={currentUserId}
            sellerId={seller.id}
            onPayAsWinner={onPayAsWinner}
          />
        </div>
      ) : null}

      <div className="lg:pt-2 lg:pb-4">
        <h2 className="text-lg font-medium mb-3 lg:mb-2 text-foreground">{tListing("description")}</h2>
        <p className="text-muted-foreground whitespace-pre-line text-base wrap-break-word overflow-wrap-anywhere">
          {description}
        </p>
      </div>

      {isPreorder && itemId ? (
        <PreorderTimelineSection listingId={itemId} className="mt-0 pt-4" />
      ) : null}

      <div className="border-t border-border pt-6 pb-6 lg:pt-4 text-sm text-muted-foreground flex flex-col gap-3 lg:gap-2 lg:order-2">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="truncate">{location}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 shrink-0" />
          <span>{tListing("listed", { time: formattedDate })}</span>
        </div>
      </div>
    </div>
  )
}
